import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, limit, startAfter, QueryDocumentSnapshot, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import type { Lead, Contact } from '../types';

const LEADS_COLLECTION = 'leads';

// Get paginated leads
export async function getLeads(pageSize = 10, lastDoc?: QueryDocumentSnapshot): Promise<{ leads: Lead[]; lastDoc?: QueryDocumentSnapshot }> {
  pageSize = Math.min(pageSize, 10); // Always limit to 10
  console.log('[API] getLeads called', { pageSize, lastDoc });
  let q = query(collection(db, LEADS_COLLECTION), orderBy('created_at', 'desc'), limit(pageSize));
  if (lastDoc) {
    q = query(collection(db, LEADS_COLLECTION), orderBy('created_at', 'desc'), startAfter(lastDoc), limit(pageSize));
  }
  const snapshot = await getDocs(q);
  const leads = snapshot.docs.map(docSnap => {
    const data = docSnap.data();
    // Convert opportunity.expected_close_date if present
    let opportunity = data.opportunity;
    if (opportunity && opportunity.expected_close_date && opportunity.expected_close_date.toDate) {
      opportunity = {
        ...opportunity,
        expected_close_date: opportunity.expected_close_date.toDate(),
      };
    }
    return {
      id: docSnap.id,
      ...data,
      opportunity,
      created_at: data.created_at?.toDate ? data.created_at.toDate() : data.created_at,
      updated_at: data.updated_at?.toDate ? data.updated_at.toDate() : data.updated_at,
    } as Lead;
  });
  const lastVisible = snapshot.docs[snapshot.docs.length - 1];
  return { leads, lastDoc: lastVisible };
}

// Add a lead
export async function addLead(lead: Omit<Lead, 'id'>) {
  console.log('[API] addLead called', { lead });
  return addDoc(collection(db, LEADS_COLLECTION), lead);
}

// Update a lead
export async function updateLead(id: string, lead: Partial<Lead>) {
  console.log('[API] updateLead called', { id, lead });
  return updateDoc(doc(db, LEADS_COLLECTION, id), lead);
}

// Delete a lead
export async function deleteLead(id: string) {
  console.log('[API] deleteLead called', { id });
  return deleteDoc(doc(db, LEADS_COLLECTION, id));
}

// Get a single lead by id
export async function getLeadById(id: string): Promise<Lead | null> {
  console.log('[API] getLeadById called', { id });
  const docRef = doc(db, LEADS_COLLECTION, id);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    const data = docSnap.data();
    let opportunity = data.opportunity;
    if (opportunity && opportunity.expected_close_date && opportunity.expected_close_date.toDate) {
      opportunity = {
        ...opportunity,
        expected_close_date: opportunity.expected_close_date.toDate(),
      };
    }
    return {
      id: docSnap.id,
      ...data,
      opportunity,
      created_at: data.created_at?.toDate ? data.created_at.toDate() : data.created_at,
      updated_at: data.updated_at?.toDate ? data.updated_at.toDate() : data.updated_at,
    } as Lead;
  }
  return null;
}

// Update lead contacts
export async function updateLeadContacts(id: string, contacts: Contact[]) {
  try {
    // Normalize contacts to ensure all fields have default values
    const normalizedContacts = contacts.map(contact => ({
      name: contact.name || '',
      position: contact.position || '',
      email: contact.email || '',
      phone: contact.phone || '',
      type: (contact.type || 'primary').toLowerCase()
    }));
    console.log('[API] updateLeadContacts called', { id, contacts: normalizedContacts });
    return await updateDoc(doc(db, LEADS_COLLECTION, id), { contacts: normalizedContacts });
  } catch (error) {
    console.error('[API] Error in updateLeadContacts:', error);
    throw error;
  }
}

// Add or update opportunity in a lead
export async function addOrUpdateOpportunity(leadId: string, opportunity: {
  stage: string;
  value: any;
  expected_close_date: Date;
  units: number;
  activities?: any[];
}) {
  try {
    console.log('[API] addOrUpdateOpportunity called', { leadId, opportunity });
    return await updateDoc(doc(db, LEADS_COLLECTION, leadId), { opportunity });
  } catch (error) {
    console.error('[API] Error in addOrUpdateOpportunity:', error);
    throw error;
  }
}

// Remove opportunity from a lead
export async function removeOpportunity(leadId: string) {
  console.log('[API] removeOpportunity called', { leadId });
  return updateDoc(doc(db, LEADS_COLLECTION, leadId), { opportunity: null });
}

// Add or update activities in a lead
export async function addOrUpdateActivities(leadId: string, activities: any[]) {
  console.log('[API] addOrUpdateActivities called', { leadId, activities });
  return updateDoc(doc(db, LEADS_COLLECTION, leadId), { activities });
}

// Get all opportunities with stage 'Closed Won' from leads
export async function getClosedWonOpportunities(): Promise<any[]> {
  const { leads } = await getLeads(100); // Fetch up to 100 leads (adjust as needed)
  return leads
    .filter(lead => lead.opportunity && lead.opportunity.stage === 'Closed Won')
    .map(lead => ({
      ...lead.opportunity,
      lead: { id: lead.id, name: lead.name },
    }));
}

// Get all leads that have an opportunity object
export async function getLeadsWithOpportunities(): Promise<Lead[]> {
  const { leads } = await getLeads(100); // Adjust as needed
  return leads.filter(lead => !!lead.opportunity);
} 